/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable no-nested-ternary */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React, {
  useEffect,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { format, parseISO } from 'date-fns';
import {
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  ReferenceArea,
  Label,
  BarChart,
} from 'recharts';
import PropTypes from 'prop-types';

import { BoxShadow } from '../../../../shared/GlobalStyles';
import Spinner from '../../../../shared/_TBComponents/Spinner';
import { CustomLegend, CustomTooltip } from '../ChartLegendsAndTooltips';

const ChartContainer = styled('div')`
  filter: ${(props) => (props.isBlurred ? 'blur(1.2px)' : null)};
  height: 450px;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;
  width: 100%;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const ChartFlexWrapper = styled('div')`
  display: flex;
  flex-direction: row;
  height: 100%;
  width: 100%;
`;

const Container = styled('div')`
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
`;

const ModalMessage = styled('div')`
  font-weight: 300;
`;

const ModalTitle = styled('div')`
  font-weight: bold;
`;

const NoDataModal = styled('div')`
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: ${BoxShadow};
  color: #424242;
  font-family: 'Nunito Sans';
  font-size: 19px;
  height: 119px;
  left: 50%;
  padding: 15px;
  position: absolute;
  text-align: center;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 400px;
`;

const YAxisLeftContainer = styled('div')`
  align-items: center;
  background-color: #FFFFFF;
  display: flex;
  height: 420px;
  left: 0;
  position: sticky;
`;

const YAxisRightContainer = styled('div')`
  align-items: center;
  background-color: #FFFFFF;
  display: flex;
  height: 420px;
  position: sticky;
  right: 0;

  /** code for seudo element scroll indicator arrow */
  @keyframes blink {
    0% {
      opacity: 0;
    }
    50% {
      opacity: ${(props) => (props.showArrow ? 1 : 0)};
    }
    100% {
      opacity: 0;
    }
  }
  ::after {
    content: '';
    border: solid 2em transparent;
    border-top-color: #9E9E9E;
    bottom: 35px;
    opacity: ${(props) => (props.showArrow ? 0.8 : 0)};
    pointer-events: none;
    position: absolute;
    right: 35px;
    rotate: -90deg;
    transform: translate(-50%,0);
    transition: all .2s ease-out;
  }
`;

const XAxisLabel = styled('div')`
  align-self: center;
  color: #9E9E9E;
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 400;
`;

const OverviewChart = ({ isAthlete }) => {
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const graphThresholds = useSelector((state) => state.vbtDashboard.data.graphThresholds);
  const group = useSelector((state) => state.vbtDashboard.data.group);
  const isAthleteSelected = useSelector((state) => state.vbtDashboard.ui.isAthleteSelected);
  const isExerciseSelected = useSelector((state) => state.vbtDashboard.ui.isExerciseSelected);
  const isVBTDataLoading = useSelector((state) => state.vbtDashboard.ui.isVBTDataLoading);
  const selectedAthlete = useSelector((state) => state.vbtDashboard.data.selectedAthlete);
  const vbtData = useSelector((state) => state.vbtDashboard.data.vbtData);
  const weightType = useSelector((state) => state.vbtDashboard.data.weightType);

  const [showArrow, setShowArrow] = useState(false);
  const [thresholdVisibilityAthlete, setThresholdVisibilityAthlete] = useState({});
  const [thresholdVisibilityGroup, setThresholdVisibilityGroup] = useState({});

  const groupChartContainerRef = useRef(null);
  const athleteChartContainerRef = useRef(null);

  // Determing weight unit label based on weight type for account
  const weightLabel = (weightType !== null
    ? weightType === 0
      ? 'lbs'
      : 'kgs'
    : currentUser?.userSettings?.weight
      ? 'kgs'
      : 'lbs');

  const checkScrollState = () => {
    const container = isAthlete ? athleteChartContainerRef.current : groupChartContainerRef.current;
    if (!container) return;

    const { scrollWidth, clientWidth: offsetWidth, scrollLeft } = container;
    const isScrollable = offsetWidth + scrollLeft !== scrollWidth;

    setShowArrow(isScrollable);
  };

  // Data transformation helpers for X axis (set number with group gaps)
  const flattenAndSortSetsWithGaps = (rawDates) => {
    if (!Array.isArray(rawDates) || rawDates.length === 0) return [];
    // Flatten all sets with their date and setNumber
    const sets = [];
    rawDates.forEach((dateEntry) => {
      if (Array.isArray(dateEntry.sets) && dateEntry.sets.length > 0) {
        dateEntry.sets.forEach((set) => {
          sets.push({
            date: dateEntry.date,
            dateSortable: parseISO(dateEntry.date).getTime(),
            formattedDate: format(parseISO(dateEntry.date), 'MMM dd'),
            load: set.load,
            meanVelocity: set.meanVelocity,
            setLength: set.setLength,
            setNumber: set.setNumber,
          });
        });
      }
    });
    // Sort by date, then setNumber
    sets.sort((a, b) => {
      if (a.dateSortable !== b.dateSortable) {
        return a.dateSortable - b.dateSortable;
      }
      return a.setNumber - b.setNumber;
    });
    // Insert gap only between different dates
    const result = [];
    let lastDate = null;
    sets.forEach((item) => {
      if (lastDate !== null && item.dateSortable !== lastDate) {
        result.push({
          isGap: true,
          meanVelocity: null,
          load: null,
        });
      }
      result.push(item);
      lastDate = item.dateSortable;
    });
    return result;
  };

  // Group and Athlete data transformation
  const groupData = flattenAndSortSetsWithGaps(vbtData?.graphData?.groupData?.dates || []);
  const athleteData = flattenAndSortSetsWithGaps(
    vbtData?.graphData?.athleteData?.find((a) => a.id === selectedAthlete?.id)?.dates || [],
  );

  const hasGroupSets = groupData.length > 0 && groupData.some((d) => !d.isGap);
  const hasAthleteSets = athleteData.length > 0 && athleteData.some((d) => !d.isGap);

  // Helper functions for axis domain rounding
  const roundUpToTenth = (num) => Math.ceil(num * 10) / 10;
  const roundUpTo5Or10 = (num) => {
    if (num <= 0) return 0;
    const remainder = num % 5;
    if (remainder === 0) return num;
    return num + (5 - remainder);
  };

  // Dynamic Y axis domains
  const getMax = (arr, key) => Math.max(
    1,
    ...arr.filter((d) => !d.isGap).map((d) => d[key] || 0),
  );
  const roundedMaxVelocity = roundUpToTenth(getMax(groupData, 'meanVelocity'));
  const roundedMaxLoad = roundUpTo5Or10(getMax(groupData, 'load'));
  const roundedMaxAthleteVelocity = roundUpToTenth(getMax(athleteData, 'meanVelocity'));
  const roundedMaxAthleteLoad = roundUpTo5Or10(getMax(athleteData, 'load'));

  // Chart width for scrollability
  const minChartWidth = Math.max(800, groupData.length * 70);
  const minAthleteChartWidth = Math.max(800, athleteData.length * 70);

  const hasGroupAndExer = group?.id && isExerciseSelected;
  const hasThresholds = graphThresholds?.length > 0;

  const defaultData = [
    { set: 1, empty: 0 },
    { set: 2, empty: 0 },
    { set: 3, empty: 0 },
    { set: 4, empty: 0 },
    { set: 5, empty: 0 },
    { set: 6, empty: 0 },
    { set: 7, empty: 0 },
    { set: 8, empty: 0 },
    { set: 9, empty: 0 },
    { set: 10, empty: 0 },
  ];

  useEffect(() => {
    if (graphThresholds) {
      const initialState = {};
      graphThresholds.forEach((_, i) => {
        initialState[i] = true;
      });
      setThresholdVisibilityAthlete(initialState);
      setThresholdVisibilityGroup(initialState);
    }
  }, [graphThresholds]);

  // UseEffects that check scrolling to show or hide scroll arrow
  useEffect(() => {
    const container = isAthlete ? athleteChartContainerRef.current : groupChartContainerRef.current;
    if (!container) return undefined;

    const handleScroll = () => {
      checkScrollState();
    };

    const handleResize = () => {
      setTimeout(() => {
        checkScrollState();
      }, 100);
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);

    return () => {
      container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [isAthlete]);

  useEffect(() => {
    const timer = setTimeout(() => {
      checkScrollState();
    }, 100);

    return () => clearTimeout(timer);
  }, [groupData, athleteData]);

  useEffect(() => {
    const timer = setTimeout(() => {
      checkScrollState();
    }, 200);

    return () => clearTimeout(timer);
  }, [isAthlete]);

  return (
    <>
      {isVBTDataLoading ? (
        <ChartContainer>
          <Spinner />
        </ChartContainer>
      ) : (
        <Container>
          {/* Group Performance Chart */}
          {hasGroupSets && !isAthlete ? (
            <>
              <CustomLegend
                onToggle={(index) => {
                  setThresholdVisibilityGroup((prev) => ({
                    ...prev,
                    [index]: !prev[index],
                  }));
                }}
                payload={[
                  {
                    color: '#FF6600',
                    dataKey: 'load',
                    value: 'Load',
                  },
                  {
                    color: '#7B61FF',
                    dataKey: 'velocity',
                    value: 'Set Avg Velocity',
                  },
                ]}
                thresholds={graphThresholds}
                thresholdVis={thresholdVisibilityGroup}
              />
              <ChartFlexWrapper id='print-group-chart'>
                {/* Sticky Left Y Axis */}
                <YAxisLeftContainer>
                  <BarChart
                    data={groupData}
                    height={440}
                    margin={{
                      top: 20, bottom: 10, right: 5, left: 5,
                    }}
                    width={65}
                  >
                    <XAxis dataKey='setNumber' hide />
                    <YAxis
                      allowDecimals
                      domain={[0, roundedMaxVelocity]}
                      interval={0}
                      label={{
                        angle: -90,
                        position: 'insideLeft',
                        style: {
                          fill: '#9E9E9E',
                          fontFamily: 'Nunito Sans',
                          fontSize: 16,
                          fontWeight: 400,
                          textAnchor: 'middle',
                        },
                        value: 'Set Avg Velocity (m/s)',
                      }}
                      tick={{ fill: '#9E9E9E', fontFamily: 'Nunito Sans', fontSize: 16 }}
                      yAxisId='left'
                    />
                  </BarChart>
                </YAxisLeftContainer>
                {/* Main Chart Scrollable Area */}
                <ChartContainer ref={groupChartContainerRef} onScroll={checkScrollState}>
                  <div style={{ minWidth: minChartWidth, height: '100%', flex: 1 }}>
                    <ResponsiveContainer>
                      <ComposedChart
                        data={groupData}
                        margin={{
                          top: 10, bottom: 0, right: 0, left: 0,
                        }}
                      >
                        <defs>
                          <linearGradient id='velocity-gradient' x1='0' y1='1' x2='0' y2='0'>
                            <stop offset='0%' stopColor='#4A3B99' stopOpacity={1} />
                            <stop offset='100%' stopColor='#7B61FF' stopOpacity={1} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray='3 3' />
                        <XAxis dataKey='setNumber' tick={{ fill: '#9E9E9E', fontFamily: 'Nunito Sans', fontSize: 16 }} />
                        <YAxis allowDecimals domain={[0, roundedMaxVelocity]} hide yAxisId='left' />
                        <YAxis allowDecimals domain={[0, roundedMaxLoad]} hide yAxisId='right' />
                        <Tooltip content={<CustomTooltip weightLabel={weightLabel} />} />
                        {/* Bars: meanVelocity */}
                        <Bar
                          barSize={100}
                          dataKey='meanVelocity'
                          fill='url(#velocity-gradient)'
                          isAnimationActive={false}
                          name='Set Avg Velocity'
                          yAxisId='left'
                        />
                        {/* Single Line: load */}
                        <Line
                          connectNulls
                          dataKey='load'
                          dot={{
                            fill: '#FF6600', r: 5, stroke: '#FF6600',
                          }}
                          isAnimationActive={false}
                          name='Load'
                          stroke='#FF6600'
                          strokeDasharray='10 10'
                          type='monotone'
                          yAxisId='right'
                        />
                        {/* ReferenceAreas for thresholds (unchanged) */}
                        {hasThresholds
                          ? graphThresholds.map((t, i) => {
                            if (thresholdVisibilityGroup?.[i] !== false) {
                              return (
                                <ReferenceArea
                                  fill='#FFCD29'
                                  fillOpacity={0.2}
                                  ifOverflow='hidden'
                                  key={`group_threshold_${i + 1}`}
                                  stroke='#FFCD29'
                                  y1={t.min}
                                  y2={t.max}
                                  yAxisId='left'
                                />
                              );
                            }
                            return null;
                          })
                          : null}
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </ChartContainer>
                {/* Sticky Right Y Axis */}
                <YAxisRightContainer showArrow={showArrow}>
                  <BarChart
                    data={groupData}
                    height={440}
                    margin={{
                      top: 20, bottom: 10, right: 5, left: 5,
                    }}
                    width={65}
                  >
                    <XAxis dataKey='setNumber' hide />
                    <YAxis
                      allowDecimals
                      domain={[0, roundedMaxLoad]}
                      interval={0}
                      label={{
                        angle: 90,
                        position: 'insideRight',
                        style: {
                          fill: '#9E9E9E',
                          fontFamily: 'Nunito Sans',
                          fontSize: 16,
                          fontWeight: 400,
                          textAnchor: 'middle',
                        },
                        value: `Load (${weightLabel})`,
                      }}
                      orientation='right'
                      tick={{
                        fill: '#9E9E9E', fontFamily: 'Nunito Sans', fontSize: 16, textAnchor: 'start',
                      }}
                      yAxisId='right'
                    />
                  </BarChart>
                </YAxisRightContainer>
              </ChartFlexWrapper>
            </>
          ) : null}
          {/* Athlete Performance Chart */}
          {hasAthleteSets && isAthlete ? (
            <>
              <CustomLegend
                onToggle={(index) => {
                  setThresholdVisibilityAthlete((prev) => ({
                    ...prev,
                    [index]: !prev[index],
                  }));
                }}
                payload={[
                  {
                    color: '#FF6600',
                    dataKey: 'load',
                    value: 'Load',
                  },
                  {
                    color: '#7B61FF',
                    dataKey: 'velocity',
                    value: 'Set Avg Velocity',
                  },
                ]}
                thresholds={graphThresholds}
                thresholdVis={thresholdVisibilityAthlete}
              />
              <ChartFlexWrapper id='print-athlete-chart'>
                {/* Sticky Left Y Axis */}
                <YAxisLeftContainer>
                  <BarChart
                    data={athleteData}
                    height={440}
                    margin={{
                      top: 20, bottom: 10, right: 5, left: 5,
                    }}
                    width={65}
                  >
                    <XAxis dataKey='setNumber' hide />
                    <YAxis
                      allowDecimals
                      domain={[0, roundedMaxAthleteVelocity]}
                      interval={0}
                      label={{
                        angle: -90,
                        position: 'insideLeft',
                        style: {
                          fill: '#9E9E9E',
                          fontFamily: 'Nunito Sans',
                          fontSize: 16,
                          fontWeight: 400,
                          textAnchor: 'middle',
                        },
                        value: 'Set Avg Velocity (m/s)',
                      }}
                      tick={{ fill: '#9E9E9E', fontFamily: 'Nunito Sans', fontSize: 16 }}
                      yAxisId='left'
                    />
                  </BarChart>
                </YAxisLeftContainer>
                {/* Main Chart Scrollable Area */}
                <ChartContainer ref={athleteChartContainerRef} onScroll={checkScrollState}>
                  <div style={{ minWidth: minAthleteChartWidth, height: '100%', flex: 1 }}>
                    <ResponsiveContainer>
                      <ComposedChart
                        data={athleteData}
                        margin={{
                          top: 10, bottom: 0, right: 0, left: 0,
                        }}
                      >
                        <defs>
                          <linearGradient id='velocity-gradient-athlete' x1='0' y1='1' x2='0' y2='0'>
                            <stop offset='0%' stopColor='#4A3B99' stopOpacity={1} />
                            <stop offset='100%' stopColor='#7B61FF' stopOpacity={1} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray='3 3' />
                        <XAxis dataKey='setNumber' tick={{ fill: '#9E9E9E', fontFamily: 'Nunito Sans', fontSize: 16 }} />
                        <YAxis allowDecimals domain={[0, roundedMaxAthleteVelocity]} hide yAxisId='left' />
                        <YAxis allowDecimals domain={[0, roundedMaxAthleteLoad]} hide yAxisId='right' />
                        <Tooltip content={<CustomTooltip weightLabel={weightLabel} />} />
                        {/* Bars: meanVelocity */}
                        <Bar
                          barSize={100}
                          dataKey='meanVelocity'
                          fill='url(#velocity-gradient-athlete)'
                          isAnimationActive={false}
                          name='Set Avg Velocity'
                          yAxisId='left'
                        />
                        {/* Single Line: load */}
                        <Line
                          connectNulls
                          dataKey='load'
                          dot={{
                            fill: '#FF6600', r: 5, stroke: '#FF6600',
                          }}
                          isAnimationActive={false}
                          name='Load'
                          stroke='#FF6600'
                          strokeDasharray='10 10'
                          type='monotone'
                          yAxisId='right'
                        />
                        {/* ReferenceAreas for thresholds */}
                        {hasThresholds
                          ? graphThresholds.map((t, i) => {
                            if (thresholdVisibilityAthlete?.[i] !== false) {
                              return (
                                <ReferenceArea
                                  fill='#FFCD29'
                                  fillOpacity={0.2}
                                  ifOverflow='hidden'
                                  key={`athlete_threshold_${i + 1}`}
                                  stroke='#FFCD29'
                                  y1={t.min}
                                  y2={t.max}
                                  yAxisId='left'
                                />
                              );
                            }
                            return null;
                          })
                          : null}
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </ChartContainer>
                {/* Sticky Right Y Axis */}
                <YAxisRightContainer showArrow={showArrow}>
                  <BarChart
                    data={athleteData}
                    height={440}
                    margin={{
                      top: 20, bottom: 10, right: 5, left: 5,
                    }}
                    width={65}
                  >
                    <XAxis dataKey='setNumber' hide />
                    <YAxis
                      allowDecimals
                      domain={[0, roundedMaxAthleteLoad]}
                      interval={0}
                      label={{
                        angle: 90,
                        position: 'insideRight',
                        style: {
                          fill: '#9E9E9E',
                          fontFamily: 'Nunito Sans',
                          fontSize: 16,
                          fontWeight: 400,
                          textAnchor: 'middle',
                        },
                        value: `Load (${weightLabel})`,
                      }}
                      orientation='right'
                      tick={{
                        fill: '#9E9E9E', fontFamily: 'Nunito Sans', fontSize: 16, textAnchor: 'start',
                      }}
                      yAxisId='right'
                    />
                  </BarChart>
                </YAxisRightContainer>
              </ChartFlexWrapper>
            </>
          ) : null}
          {/* No Data Chart & Modals */}
          {!hasGroupSets || (!hasAthleteSets && isAthlete) ? (
            <ChartContainer isBlurred>
              <ResponsiveContainer>
                <LineChart data={defaultData}>
                  <CartesianGrid strokeDasharray='3 3' />
                  <XAxis
                    dataKey='set'
                    height={50}
                    label={{
                      offset: 0,
                      position: 'insideBottom',
                      value: 'Set Number',
                      style: {
                        fill: '#9E9E9E',
                        fontFamily: 'Nunito Sans',
                        fontSize: 16,
                        fontWeight: 400,
                        textAnchor: 'middle',
                      },
                    }}
                    padding={{ left: 15, right: 15 }}
                  />
                  <YAxis domain={[0, 1.5]} yAxisId='left'>
                    <Label
                      value='Set Avg Velocity (m/s)'
                      position='insideLeft'
                      angle={-90}
                      style={{
                        fill: '#9E9E9E',
                        fontFamily: 'Nunito Sans',
                        fontSize: 16,
                        fontWeight: 400,
                        textAnchor: 'middle',
                      }}
                    />
                  </YAxis>
                  <YAxis
                    domain={[0, 450]}
                    label={{
                      angle: 90,
                      position: 'insideRight',
                      value: `Load (${weightLabel})`,
                      style: {
                        fill: '#9E9E9E',
                        fontFamily: 'Nunito Sans',
                        fontSize: 16,
                        fontWeight: 400,
                        textAnchor: 'middle',
                      },
                    }}
                    orientation='right'
                    width={65}
                    yAxisId='right'
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartContainer>
          ) : null}
          {!hasGroupSets || (!hasGroupAndExer && isAthlete) ? (
            <NoDataModal>
              <ModalTitle>
                {!hasGroupAndExer ? 'Select a Group and Exercise' : 'No Data Available'}
              </ModalTitle>
              <ModalMessage>
                {!hasGroupAndExer
                  ? 'Select a group and exercise using the dropdowns above to get started'
                  : 'There is no data available, please adjust your selections above'}
              </ModalMessage>
            </NoDataModal>
          ) : null}
          {hasGroupAndExer && !hasAthleteSets && isAthlete ? (
            <NoDataModal>
              <ModalTitle>
                {!isAthleteSelected && 'Select an Athlete'}
                {(!hasAthleteSets && isAthleteSelected) && 'No Data Available'}
              </ModalTitle>
              <ModalMessage>
                {!isAthleteSelected && 'Select a group and athlete using the dropdowns above to get started'}
                {(!hasAthleteSets && isAthleteSelected) && 'There is no data available, please adjust your selections above'}
              </ModalMessage>
            </NoDataModal>
          ) : null}
          {hasGroupSets && !isAthlete ? (
            <XAxisLabel>Set Number</XAxisLabel>
          ) : null}
          {hasAthleteSets && isAthlete ? (
            <XAxisLabel>Set Number</XAxisLabel>
          ) : null}
        </Container>
      )}
    </>
  );
};

OverviewChart.propTypes = {
  isAthlete: PropTypes.bool,
};

OverviewChart.defaultProps = {
  isAthlete: false,
};

export default OverviewChart;
